import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MySnackbar from "../../components/UI/MySnackBar";
import { RootCeleste } from "../../components/Root";
import { HomeViewTitle } from "../../styles/Titles";
import {
  AVAILABILITY_REQUEST_TEMPLATE,
  FINAL_DELIVERY_TEMPLATE,
  REVIEWER_ASSIGNMENT_TEMPLATE,
  getEmailTemplate,
  updateEmailTemplate,
} from "../../api/emailTemplates";

const Root = RootCeleste;
const Title = HomeViewTitle;

// Misma sintaxis que string.Template en el backend: ${nombre} o $nombre.
// Se usa solo para la vista previa y para avisar de una variable desconocida
// antes de guardar; la validación que manda es la del servidor.
const VARIABLE_PATTERN = /\$(?:\{([a-zA-Z_][a-zA-Z0-9_]*)\}|([a-zA-Z_][a-zA-Z0-9_]*))/g;

const findIdentifiers = (text) => {
  const found = [];
  for (const match of (text || "").matchAll(VARIABLE_PATTERN)) {
    found.push(match[1] || match[2]);
  }
  return found;
};

const substitute = (text, values) =>
  (text || "").replace(VARIABLE_PATTERN, (whole, braced, bare) => {
    const name = braced || bare;
    return name in values ? values[name] : whole;
  });

// Lo que cambia entre una plantilla y otra: qué se edita y cómo se explica.
const TEMPLATE_TEXTS = {
  [REVIEWER_ASSIGNMENT_TEMPLATE]: {
    title: "Plantilla del aviso de revisor",
    intro: (
      <>
        Es el mail que reciben los estudiantes, el director y el revisor de un equipo
        cuando apretás <strong>Avisar</strong> en la tabla de anteproyectos.
      </>
    ),
    linksHelp: "El link al PDF se vuelve clickeable solo.",
  },
  [FINAL_DELIVERY_TEMPLATE]: {
    title: "Plantilla del aviso de entrega final",
    intro: (
      <>
        Es el mail que reciben los estudiantes, el director y los evaluadores de un
        equipo cuando apretás <strong>Avisar</strong> en la tabla de entregas finales,
        con los links para descargar los tres documentos.
      </>
    ),
    linksHelp: "Los links a los documentos se vuelven clickeables solos.",
  },
  [AVAILABILITY_REQUEST_TEMPLATE]: {
    title: "Plantilla del pedido de disponibilidad",
    intro: (
      <>
        Es el mail que recibe cada director o evaluador que todavía no cargó su
        disponibilidad, cuando apretás <strong>Pedir disponibilidad</strong> en la
        pantalla del algoritmo de fechas. Sale uno por persona.
      </>
    ),
    linksHelp: "El link al sistema se vuelve clickeable solo.",
  },
};

/**
 * Editor de una plantilla de mail editable (aviso de revisor o de entrega final).
 *
 * Tres columnas de trabajo: el formulario (asunto, cuerpo, copia oculta), las
 * variables disponibles con su explicación y un ejemplo (clic para insertar
 * donde está el cursor), y una vista previa que se rellena con los ejemplos
 * mientras se escribe. Las variables desconocidas se marcan antes de guardar;
 * el servidor las vuelve a validar y devuelve el motivo si algo no cierra.
 */
const EmailTemplateView = ({ templateKey = REVIEWER_ASSIGNMENT_TEMPLATE }) => {
  const texts = TEMPLATE_TEXTS[templateKey];
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bcc, setBcc] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", status: "" });

  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const [lastFocused, setLastFocused] = useState("body");

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEmailTemplate(user, templateKey);
        setTemplate(data);
        setSubject(data.subject);
        setBody(data.body);
        setBcc(data.bcc || "");
        setReplyTo(data.reply_to || "");
      } catch (e) {
        setLoadError(
          e?.response?.data?.detail || "No se pudo cargar la plantilla. Inténtalo de nuevo."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, templateKey]);

  const variables = template?.variables || [];
  const knownNames = useMemo(() => new Set(variables.map((v) => v.name)), [variables]);
  const exampleValues = useMemo(
    () => Object.fromEntries(variables.map((v) => [v.name, v.example])),
    [variables]
  );

  const unknown = useMemo(() => {
    const names = [...findIdentifiers(subject), ...findIdentifiers(body)];
    return [...new Set(names.filter((n) => !knownNames.has(n)))];
  }, [subject, body, knownNames]);

  const dirty =
    template &&
    (subject !== template.subject ||
      body !== template.body ||
      bcc !== (template.bcc || "") ||
      replyTo !== (template.reply_to || ""));

  const isDefault =
    template && subject === template.default_subject && body === template.default_body;

  const insertVariable = useCallback(
    (name) => {
      const token = "${" + name + "}";
      const target = lastFocused === "subject" ? subjectRef.current : bodyRef.current;
      const setter = lastFocused === "subject" ? setSubject : setBody;
      const current = lastFocused === "subject" ? subject : body;

      if (!target) {
        setter(current + token);
        return;
      }
      const start = target.selectionStart ?? current.length;
      const end = target.selectionEnd ?? current.length;
      const next = current.slice(0, start) + token + current.slice(end);
      setter(next);
      // Volver a dejar el cursor después de lo insertado
      requestAnimationFrame(() => {
        target.focus();
        target.setSelectionRange(start + token.length, start + token.length);
      });
    },
    [lastFocused, subject, body]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await updateEmailTemplate(user, templateKey, {
        subject,
        body,
        bcc,
        reply_to: replyTo,
      });
      setTemplate(saved);
      setSubject(saved.subject);
      setBody(saved.body);
      setBcc(saved.bcc || "");
      setReplyTo(saved.reply_to || "");
      setNotification({ open: true, message: "Plantilla guardada.", status: "success" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setNotification({
        open: true,
        message: detail || "No se pudo guardar la plantilla. Inténtalo de nuevo.",
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    if (!template) return;
    setSubject(template.default_subject);
    setBody(template.default_body);
  };

  if (loading) {
    return (
      <Root maxWidth="lg">
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  return (
    <Root maxWidth="lg">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small">
          Volver
        </Button>
      </Stack>
      <Title variant="h4">{texts.title}</Title>
      <Typography variant="body1" sx={{ color: "#555", mb: 3 }}>
        {texts.intro} Escribí el texto una vez; el sistema lo completa con los datos de
        cada equipo.
      </Typography>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Grid container spacing={3}>
        {/* Formulario */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <TextField
              label="Asunto"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              inputRef={subjectRef}
              onFocus={() => setLastFocused("subject")}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Cuerpo"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              inputRef={bodyRef}
              onFocus={() => setLastFocused("body")}
              fullWidth
              multiline
              minRows={14}
              margin="normal"
              helperText={`Texto plano. Las líneas en blanco separan párrafos. ${texts.linksHelp}`}
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: 14 } }}
            />
            <TextField
              label="Copia oculta (CCO / BCC)"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="catedra@fi.uba.ar, otra@fi.uba.ar"
              helperText="Direcciones que reciben copia sin aparecer entre los destinatarios. Separadas por coma. Puede quedar vacío."
            />
            <TextField
              label="Responder a (Reply-To)"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="catedra@fi.uba.ar"
              helperText="A dónde llegan las respuestas. El remitente es una casilla no-reply que no recibe correo: sin esto, contestar rebota. Si queda vacío, se usa el mail del admin que manda. Se puede cambiar en cada envío."
            />

            {unknown.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Variables que no existen y van a salir tal cual:{" "}
                {unknown.map((n) => (
                  <code key={n} style={{ marginRight: 6 }}>{"${" + n + "}"}</code>
                ))}
                Revisá la lista de la derecha. Para escribir un signo $ literal, usá $$.
              </Alert>
            )}

            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
              <Tooltip title={isDefault ? "Ya es la plantilla por defecto" : "Vuelve al texto original del sistema. No se guarda hasta que aprietes Guardar."}>
                <span>
                  <Button
                    startIcon={<RestoreIcon />}
                    onClick={handleRestore}
                    disabled={isDefault}
                    color="inherit"
                  >
                    Restaurar por defecto
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !dirty || unknown.length > 0}
              >
                Guardar
              </Button>
            </Stack>
            {template?.updated_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Última modificación: {new Date(template.updated_at).toLocaleString("es-AR")}
              </Typography>
            )}
            {template && !template.updated_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Todavía no se editó: se está usando la plantilla por defecto del sistema.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Variables + vista previa */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Variables</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Se escriben como <code>$&#123;nombre&#125;</code> y se reemplazan por el dato de
              cada equipo al mandar. Hacé clic en una para insertarla donde está el cursor.
            </Typography>
            <Stack spacing={1.25}>
              {variables.map((v) => (
                <Box key={v.name}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={"${" + v.name + "}"}
                      onClick={() => insertVariable(v.name)}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ fontFamily: "monospace", cursor: "pointer" }}
                    />
                    <Typography variant="body2">{v.description}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, wordBreak: "break-all" }}>
                    Ejemplo: {v.example}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Vista previa</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Con datos de ejemplo. Antes de mandar a un equipo real vas a ver la vista
              previa con sus datos.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Asunto:</strong> {substitute(subject, exampleValues)}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", backgroundColor: "#fafafa", p: 1.5, borderRadius: 1 }}
            >
              {substitute(body, exampleValues)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <MySnackbar
        open={notification.open}
        handleClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        status={notification.status}
      />
    </Root>
  );
};

export default EmailTemplateView;
